// §4 옵션/재고 — 조합(combination) variant 와 일반(평면) variant 의 렌더 분리.
// GET option-groups 의 variantCombinations[].variantId 가 조합-생성 variant 의 식별자(정본).
// 분류는 **렌더 전용**이다 — 폼 상태는 단일 variants 배열을 유지하고, 페이로드(detailMapper
// buildUpdateVariants)는 조합/일반 전 행을 종전과 동일하게 전송한다(stock-only patch 채널).

export interface VariantEntryLike {
  // exactOptionalPropertyTypes — RHF field 타입의 `variantId?: string | null | undefined` 수용.
  variantId?: string | null | undefined;
}

export interface IndexedVariantEntry<T extends VariantEntryLike> {
  field: T;
  /** variants 폼 배열에서의 현재 인덱스 — FormField name 경로(`variants.${index}.*`)에 쓴다. */
  index: number;
}

export interface PartitionedVariantEntries<T extends VariantEntryLike> {
  flat: ReadonlyArray<IndexedVariantEntry<T>>;
  combo: ReadonlyArray<IndexedVariantEntry<T>>;
}

/** variantCombinations 응답에서 조합 variant id 집합을 만든다(빈/누락 id 는 제외). */
export const comboVariantIdSet = (
  combinations: ReadonlyArray<{ variantId: string }> | undefined,
): ReadonlySet<string> =>
  new Set((combinations ?? []).map((c) => c.variantId).filter((id) => id.length > 0));

/**
 * variants 폼 배열을 조합/일반으로 분리한다. variantId 가 조합 집합에 있으면 combo,
 * 그 외(신규 행의 variantId=null 포함)는 flat. 원본 인덱스를 보존해 RHF 경로가 깨지지 않는다.
 */
export const partitionVariantEntries = <T extends VariantEntryLike>(
  fields: ReadonlyArray<T>,
  comboIds: ReadonlySet<string>,
): PartitionedVariantEntries<T> => {
  const flat: IndexedVariantEntry<T>[] = [];
  const combo: IndexedVariantEntry<T>[] = [];
  fields.forEach((field, index) => {
    const id = field.variantId;
    if (id != null && comboIds.has(id)) {
      combo.push({ field, index });
    } else {
      flat.push({ field, index });
    }
  });
  return { flat, combo };
};

/** 그룹-한정 라벨 쌍 — "색상: 차콜" 의 (그룹명, 선택지 라벨). */
export interface ComboLabelPair {
  groupName: string;
  choiceLabel: string;
}

/** 등록 미리보기용 옵션 그룹 입력(최소 형태) — name + choices[label]. */
export interface OptionGroupPreviewInput {
  name?: string | null;
  choices?: ReadonlyArray<{ label?: string | null }> | null;
}

/**
 * QA-R6 G — 등록 시점 조합 미리보기. 옵션 그룹들의 데카르트 곱을 그룹-한정 라벨 쌍 배열로 만든다.
 * (서버가 저장 시 실제 조합 variant 를 생성하기 전, 운영자가 "어떤 조합이 생길지"를 미리 보도록.)
 * 라벨이 비어 있는 선택지/그룹은 미완성으로 보고 제외하며, 유효 선택지가 1개도 없는 그룹이 있으면
 * 그 그룹을 건너뛴다. 유효 그룹이 0개면 빈 배열(아직 입력 중). 상한(maxCombos, 기본 100=BE 정합) 초과 시 잘라낸다.
 */
export const buildComboPreviewPairs = (
  groups: ReadonlyArray<OptionGroupPreviewInput>,
  maxCombos = 100,
): ComboLabelPair[][] => {
  const cleaned = groups
    .map((g) => ({
      name: (g.name ?? '').trim(),
      labels: (g.choices ?? [])
        .map((c) => (c.label ?? '').trim())
        .filter((l) => l.length > 0),
    }))
    .filter((g) => g.name.length > 0 && g.labels.length > 0);
  if (cleaned.length === 0) return [];

  let acc: ComboLabelPair[][] = [[]];
  for (const group of cleaned) {
    const next: ComboLabelPair[][] = [];
    for (const combo of acc) {
      for (const label of group.labels) {
        if (next.length >= maxCombos) break;
        next.push([...combo, { groupName: group.name, choiceLabel: label }]);
      }
    }
    acc = next;
    if (acc.length >= maxCombos) break;
  }
  return acc;
};

/**
 * QA-R6 G(옵션 A) — 조합의 안정 키. 그룹 순서대로의 (그룹명, 선택지 라벨) 쌍을 구분자로 이어 만든다.
 * 등록 화면에서 조합별 재고를 폼 상태(comboStocks[key])에 보관/조회할 때 쓰며, 같은 조합이면 입력 순서·
 * 그룹 리네이밍과 무관하게 동일 키를 보장한다(그룹명·라벨이 키의 일부라, 라벨을 바꾸면 새 조합으로 본다 —
 * 등록 시점엔 아직 영속 id 가 없어 라벨이 유일 식별자이므로 의도된 동작). 구분자는 입력에 나오기 어려운
 * 제어문자(US/RS)를 써 충돌을 막는다.
 */
const COMBO_KEY_PAIR_SEP = ''; // RS — 쌍 사이.
const COMBO_KEY_KV_SEP = ''; // US — 그룹명/라벨 사이.
export const comboKeyOf = (pairs: ReadonlyArray<ComboLabelPair>): string =>
  pairs
    .map((p) => `${p.groupName}${COMBO_KEY_KV_SEP}${p.choiceLabel}`)
    .join(COMBO_KEY_PAIR_SEP);

/**
 * 조합의 (groupId, choiceId) 링크를 그룹-한정 라벨 쌍으로 해석한다 — 다중 그룹(색상×사이즈)에서
 * 선택지 라벨만으로는 행이 어느 그룹의 값인지 모호하다는 사용자 피드백 대응. 그룹 배열 순서
 * (=BE position 정렬 응답 순서)를 보존한다. 링크가 하나라도 해석 불가(그룹/선택지 미존재·
 * 이름/라벨 공백)면 null — 부분 표기는 거짓 정보라 호출측이 variant 원본 라벨로 폴백한다
 * (레거시 안전).
 */
export const resolveComboLabelPairs = (
  combination: { choices: ReadonlyArray<{ groupId: string; choiceId: string }> },
  groups: ReadonlyArray<{
    groupId: string;
    name: string;
    choices: ReadonlyArray<{ choiceId: string; label: string }>;
  }>,
): ComboLabelPair[] | null => {
  if (combination.choices.length === 0 || groups.length === 0) return null;
  const unresolved = new Set(combination.choices.map((c) => `${c.groupId} ${c.choiceId}`));
  const pairs: ComboLabelPair[] = [];
  for (const group of groups) {
    const link = combination.choices.find((c) => c.groupId === group.groupId);
    if (!link) continue;
    const choice = group.choices.find((ch) => ch.choiceId === link.choiceId);
    if (!choice || !group.name.trim() || !choice.label.trim()) return null;
    pairs.push({ groupName: group.name, choiceLabel: choice.label });
    unresolved.delete(`${link.groupId} ${link.choiceId}`);
  }
  // 미해석 링크 잔존(미존재 그룹 참조/동일 그룹 중복 링크) — 부분 표기 대신 폴백.
  if (unresolved.size > 0 || pairs.length === 0) return null;
  return pairs;
};
